import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { authApi } from "../api";
import toast from "react-hot-toast";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check authentication status on mount
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            checkAuth();
        } else {
            setLoading(false);
        }
    }, []); // Only run once on mount

    const checkAuth = async () => {
        const token = localStorage.getItem("token");
        
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await authApi.checkAuth();

            if (response.success) {
                setUser(response.data);
                setIsAuthenticated(true);
                toast.success("Welcome back!");
            } else {
                localStorage.removeItem("token");
                setUser(null);
                setIsAuthenticated(false);
                toast.error("Session expired. Please log in again.");
            }
        } catch (error) {
            if (
                error.response?.status === 401 ||
                error.response?.status === 403
            ) {
                localStorage.removeItem("token");
                setUser(null);
                setIsAuthenticated(false);
                toast.error("Session expired. Please log in again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await authApi.login(email, password);

        if (response.success) {
            const { user, token } = response.data;
            localStorage.setItem("token", token);
            setUser(user);
            setIsAuthenticated(true);
            return { success: true, user };
        } else {
            return { success: false, message: response.message };
        }
    };

    const register = async (firstName, lastName, email, password) => {
        const response = await authApi.register({ firstName, lastName, email, password });

        if (response.success) {
            const { user, token } = response.data;
            localStorage.setItem("token", token);
            setUser(user);
            setIsAuthenticated(true);
            return { success: true, user };
        } else {
            return { success: false, message: response.message };
        }
    };

    const logout = () => {
        localStorage.removeItem("token");
        setUser(null);
        setIsAuthenticated(false);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isAuthenticated,
                login,
                register,
                logout,
                checkAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
